import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, PlayArrow as PlayIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { api, endpoints } from '../../services/api';
import { hasCapability, getCapabilitiesFromUser } from '../../auth/access';
import { useAuth } from '../../contexts/AuthContext';
import {
  defaultStrictProfileForDevice,
  enterpriseAcceptancePassed,
  enterpriseJobChipColor,
  EnterpriseDeployDevice,
  EnterpriseDeployJob,
  isEnterpriseJobRunning,
  isEnterpriseJobTerminal,
  resolveEnterpriseEdgeHost,
} from './enterpriseDeployShared';

const STRICT_PROFILE_OPTIONS = [
  { value: 'warehouse_v1_fanless', label: 'warehouse_v1_fanless (fanless / Rubik Pi)' },
  { value: 'warehouse_v1', label: 'warehouse_v1 (active cooling)' },
  { value: 'warehouse_v1_no_camera', label: 'warehouse_v1_no_camera' },
];

const MODEL_BUNDLE_OPTIONS = [
  { value: 'current', label: 'current (VM mount)' },
];

type Props = {
  deviceId: string;
  device?: EnterpriseDeployDevice | null;
  /** Compact layout for Bring Up “Finish” section */
  variant?: 'default' | 'compact';
  /** Show post–bring-up CTA copy when commissioning may be done */
  showPostBringUpCta?: boolean;
  /** Notify parent when latest job payload changes (e.g. Setup home gate). */
  onJobChange?: (job: EnterpriseDeployJob | null) => void;
};

export const EnterpriseProductionDeployPanel: React.FC<Props> = ({
  deviceId,
  device,
  variant = 'default',
  showPostBringUpCta = false,
  onJobChange,
}) => {
  const { effectiveUser: user } = useAuth();
  const capabilities = useMemo(() => getCapabilitiesFromUser(user), [user]);
  const canReadDeployments = hasCapability(capabilities, 'deployments:read');
  const canWriteDeployments = hasCapability(capabilities, 'deployments:write');

  const resolvedHost = useMemo(() => resolveEnterpriseEdgeHost(device), [device]);
  const defaultStrict = useMemo(() => defaultStrictProfileForDevice(device), [device]);

  const [releaseId, setReleaseId] = useState('');
  const [strictProfile, setStrictProfile] = useState(defaultStrict);
  const [modelBundleRef, setModelBundleRef] = useState('current');
  const [job, setJob] = useState<EnterpriseDeployJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setStrictProfile(defaultStrict);
  }, [defaultStrict, deviceId]);

  const loadLatest = useCallback(async () => {
    if (!deviceId || !canReadDeployments) return;
    setLoadError(null);
    try {
      const res = await api.get(endpoints.enterpriseDeploy.latest(deviceId));
      const data = res.data as EnterpriseDeployJob;
      if (data?.error === 'no_enterprise_deploy_result') {
        setJob(null);
        onJobChange?.(null);
      } else {
        setJob(data || null);
        onJobChange?.(data || null);
        if (data?.release_id) {
          setReleaseId((prev) => (prev.trim() ? prev : String(data.release_id)));
        }
      }
    } catch (e: any) {
      setLoadError(e?.response?.data?.error || e?.message || 'enterprise_latest_failed');
    }
  }, [deviceId, canReadDeployments, onJobChange]);

  useEffect(() => {
    if (!deviceId) {
      setJob(null);
      return;
    }
    void loadLatest();
  }, [deviceId, loadLatest]);

  useEffect(() => {
    if (!deviceId || !job || !isEnterpriseJobRunning(job)) return;
    const timer = window.setInterval(() => {
      void loadLatest();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [deviceId, job, loadLatest]);

  const jobStatus = String(job?.status || '').toLowerCase();
  const jobRunning = isEnterpriseJobRunning(job);
  const jobTerminal = isEnterpriseJobTerminal(job);
  const acceptancePass = enterpriseAcceptancePassed(job);

  async function startProductionDeploy() {
    const rid = releaseId.trim();
    if (!rid) {
      setActionError('Release ID is required (e.g. edge_rel_20260518_001).');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const body: Record<string, unknown> = {
        release_id: rid,
        strict_profile: strictProfile,
        model_bundle_ref: modelBundleRef === 'current' ? 'current' : modelBundleRef,
        skip_cloud_audit: true,
        wait: false,
      };
      const res = await api.post(endpoints.enterpriseDeploy.start(deviceId), body);
      setJob((res.data as EnterpriseDeployJob) || null);
      await loadLatest();
    } catch (e: any) {
      const status = e?.response?.status;
      const err = e?.response?.data?.error || e?.message || 'enterprise_deploy_failed';
      if (status === 403) {
        setActionError('Access denied. This action requires deployments:write on your account or API key.');
      } else {
        setActionError(String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function downloadAcceptance() {
    setActionError(null);
    try {
      const params: Record<string, string> = {};
      if (job?.job_id) params.job_id = String(job.job_id);
      const res = await api.get(endpoints.enterpriseDeploy.acceptance(deviceId), { params });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `acceptance_${deviceId}_${job?.job_id || 'latest'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setActionError(e?.response?.data?.error || e?.message || 'acceptance_download_failed');
    }
  }

  if (!deviceId) {
    return null;
  }

  if (!canReadDeployments) {
    return (
      <Alert severity="info">
        Production deploy (Enterprise) requires <strong>deployments:read</strong> to view job status.
      </Alert>
    );
  }

  const compact = variant === 'compact';

  return (
    <Paper
      id="enterprise-production-deploy"
      variant={compact ? 'outlined' : 'elevation'}
      sx={{ p: compact ? 1.5 : 2, mt: compact ? 0 : undefined }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant={compact ? 'subtitle1' : 'h6'} gutterBottom={!compact}>
            Production deploy (Enterprise)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            First go-live or re-commission: model bundle, strict gate, and post-deploy acceptance — same bar as{' '}
            <code>enterprise_v1.sh</code>. For day-2 updates use <strong>Software update</strong> / Release OTA instead.
          </Typography>
          {showPostBringUpCta && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Bring-up installs runtime via software update. Run production deploy when you are ready for the full
              enterprise acceptance gate (models + strict profile).
            </Alert>
          )}
        </Box>

        {resolvedHost ? (
          <Typography variant="caption" color="text.secondary">
            Cloud will reach the edge via registry / Tailscale host ({resolvedHost}). You do not need LAN access from
            your browser.
          </Typography>
        ) : (
          <Alert severity="warning">
            No <code>health_api_host</code> or IP on this device. Set Tailscale IP in device metadata before deploying.
          </Alert>
        )}

        {!canWriteDeployments && (
          <Alert severity="info">You have read-only deployment access. Starting production deploy is disabled.</Alert>
        )}

        {loadError && (
          <Alert severity="warning" onClose={() => setLoadError(null)}>
            {loadError}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-start">
          <TextField
            size="small"
            label="Release ID"
            required
            fullWidth
            value={releaseId}
            onChange={(e) => setReleaseId(e.target.value)}
            placeholder="edge_rel_YYYYMMDD_NNN"
            disabled={!canWriteDeployments || busy || jobRunning}
            helperText="Prod release id passed to enterprise_v1.sh"
          />
          <FormControl size="small" fullWidth disabled={!canWriteDeployments || busy || jobRunning}>
            <InputLabel>Strict profile</InputLabel>
            <Select
              label="Strict profile"
              value={strictProfile}
              onChange={(e) => setStrictProfile(String(e.target.value))}
            >
              {STRICT_PROFILE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!canWriteDeployments || busy || jobRunning}>
            <InputLabel>Model bundle</InputLabel>
            <Select
              label="Model bundle"
              value={modelBundleRef}
              onChange={(e) => setModelBundleRef(String(e.target.value))}
            >
              {MODEL_BUNDLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
            onClick={() => void startProductionDeploy()}
            disabled={!canWriteDeployments || busy || jobRunning || !releaseId.trim()}
          >
            {jobRunning ? 'Deploy running…' : 'Start production deploy'}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadLatest()} disabled={busy}>
            Refresh status
          </Button>
          {job && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => void downloadAcceptance()}
              disabled={!acceptancePass && !job?.acceptance}
            >
              Download acceptance JSON
            </Button>
          )}
        </Stack>

        {job && (
          <>
            <Divider />
            {jobRunning && <LinearProgress />}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2">Job</Typography>
              <Chip size="small" label={jobStatus || 'unknown'} color={enterpriseJobChipColor(jobStatus)} />
              {job.job_id && (
                <Typography variant="caption" color="text.secondary">
                  {job.job_id}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {job.release_id && <Chip size="small" label={`release: ${job.release_id}`} variant="outlined" />}
              {job.bundle_hash && <Chip size="small" label={`bundle: ${job.bundle_hash}`} variant="outlined" />}
              {job.strict_profile && (
                <Chip size="small" label={`profile: ${job.strict_profile}`} variant="outlined" />
              )}
            </Stack>
            {Array.isArray(job.steps) &&
              job.steps.map((step, idx) => (
                <Typography key={`${step.step}-${idx}`} variant="body2" color="text.secondary">
                  {step.step}: {step.status}
                  {step.rc != null ? ` (rc=${step.rc})` : ''}
                </Typography>
              ))}
            {jobTerminal && acceptancePass && (
              <Alert severity="success">
                Acceptance passed ({String(job.acceptance?.overall || 'pass')}). Device meets enterprise production bar.
              </Alert>
            )}
            {jobTerminal && !acceptancePass && (
              <Alert severity="error">
                Deploy finished without passing acceptance.
                {job.error ? ` ${job.error}` : ''}
                {job.detail ? ` — ${String(job.detail).slice(0, 200)}` : ''}
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default EnterpriseProductionDeployPanel;
